import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from './useAuthStore';
import { useUserRoles } from './useUserRoles';

interface LegalDocument {
  id: string;
  name: string;
  version: string;
  content_md: string;
}

interface CompanyNDAStatus {
  companyId: string;
  companyName: string;
  needsAcceptance: boolean;
}

interface NDAComplianceState {
  needsAcceptance: boolean;
  loading: boolean;
  activeNDA: LegalDocument | null;
  error: string | null;
  pendingCompanies: CompanyNDAStatus[];
  currentCompanyIndex: number;
  totalPendingCount: number;
}

export function useNDACompliance(companyId?: string): NDAComplianceState {
  const { user } = useAuthStore();
  const { hasRole, loading: rolesLoading } = useUserRoles(companyId);
  const [state, setState] = useState<NDAComplianceState>({
    needsAcceptance: false,
    loading: true,
    activeNDA: null,
    error: null,
    pendingCompanies: [],
    currentCompanyIndex: 0,
    totalPendingCount: 0,
  });

  useEffect(() => {
    async function checkCompliance() {
      if (!user || rolesLoading) {
        setState({
          needsAcceptance: false,
          loading: false,
          activeNDA: null,
          error: null,
          pendingCompanies: [],
          currentCompanyIndex: 0,
          totalPendingCount: 0,
        });
        return;
      }

      try {
        // If specific company provided, check that one
        if (companyId) {
          const requiresNDA = hasRole('investor', companyId) || hasRole('observer', companyId);

          if (!requiresNDA) {
            setState({
              needsAcceptance: false,
              loading: false,
              activeNDA: null,
              error: null,
              pendingCompanies: [],
              currentCompanyIndex: 0,
              totalPendingCount: 0,
            });
            return;
          }
        }

        // Fetch active NDA for this company
        const { data: activeNDA, error: ndaError } = await supabase
          .from('legal_documents')
          .select('id, name, version, content_md')
          .eq('document_type', 'NDA')
          .is('active_until', null)
          .single();

        if (ndaError) {
          console.error('Error fetching active NDA:', ndaError);
          setState({
            needsAcceptance: false,
            loading: false,
            activeNDA: null,
            error: 'Failed to load NDA document',
            pendingCompanies: [],
            currentCompanyIndex: 0,
            totalPendingCount: 0,
          });
          return;
        }

        if (!activeNDA) {
          setState({
            needsAcceptance: false,
            loading: false,
            activeNDA: null,
            error: null,
            pendingCompanies: [],
            currentCompanyIndex: 0,
            totalPendingCount: 0,
          });
          return;
        }

        // Get all companies where user is investor/observer
        const { data: userRoles } = await supabase
          .from('user_roles')
          .select('company_id, companies(name)')
          .eq('user_id', user.id)
          .in('role', ['investor', 'observer'])
          .not('company_id', 'is', null);

        if (!userRoles || userRoles.length === 0) {
          setState({
            needsAcceptance: false,
            loading: false,
            activeNDA: null,
            error: null,
            pendingCompanies: [],
            currentCompanyIndex: 0,
            totalPendingCount: 0,
          });
          return;
        }

        // Check which companies need NDA acceptance
        const pendingCompanies: CompanyNDAStatus[] = [];

        for (const role of userRoles) {
          if (!role.company_id) continue;

          const { data: acceptance } = await supabase
            .from('nda_acceptances')
            .select('id')
            .eq('user_id', user.id)
            .eq('company_id', role.company_id)
            .eq('nda_version', activeNDA.id)
            .maybeSingle();

          if (!acceptance) {
            pendingCompanies.push({
              companyId: role.company_id,
              companyName: (role.companies as any)?.name || 'Unknown Company',
              needsAcceptance: true,
            });
          }
        }

        // If checking specific company, find its index
        let currentIndex = 0;
        if (companyId) {
          currentIndex = pendingCompanies.findIndex((c) => c.companyId === companyId);
          if (currentIndex === -1) currentIndex = 0;
        }

        setState({
          needsAcceptance: pendingCompanies.length > 0,
          loading: false,
          activeNDA: activeNDA,
          error: null,
          pendingCompanies,
          currentCompanyIndex: currentIndex,
          totalPendingCount: pendingCompanies.length,
        });
      } catch (error) {
        console.error('Error in NDA compliance check:', error);
        setState({
          needsAcceptance: false,
          loading: false,
          activeNDA: null,
          error: 'An unexpected error occurred',
          pendingCompanies: [],
          currentCompanyIndex: 0,
          totalPendingCount: 0,
        });
      }
    }

    checkCompliance();
  }, [user, companyId, hasRole, rolesLoading]);

  return state;
}
